const noop = (): void => {
  //
};

const MyProxy = (): anyOk =>
  new Proxy(noop, {
    get: (): anyOk => MyProxy(),
  });

const winProxy = MyProxy();

export interface Fullstory {
  identify(uid: string, opts: { displayName?: string; email?: string }): void;
  setUserVars(vars: Record<string, anyOk>): void;
  getCurrentSessionURL(): string;
  event(name: string, properties: Record<string, anyOk>): void;
  restart(): void;
}

interface SpeechRecognition extends EventTarget {
  grammars: SpeechGrammarList;
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  serviceURI: string;

  start(): void;
  stop(): void;
  abort(): void;

  onaudiostart: (this: SpeechRecognition, ev: Event) => anyOk;
  onsoundstart: (this: SpeechRecognition, ev: Event) => anyOk;
  onspeechstart: (this: SpeechRecognition, ev: Event) => anyOk;
  onspeechend: (this: SpeechRecognition, ev: Event) => anyOk;
  onsoundend: (this: SpeechRecognition, ev: Event) => anyOk;
  onaudioend: (this: SpeechRecognition, ev: Event) => anyOk;
  onresult: (this: SpeechRecognition, ev: SpeechRecognitionEvent) => anyOk;
  onnomatch: (this: SpeechRecognition, ev: SpeechRecognitionEvent) => anyOk;
  onerror: (this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => anyOk;
  onstart: (this: SpeechRecognition, ev: Event) => anyOk;
  onend: (this: SpeechRecognition, ev: Event) => anyOk;
}

interface webkitSpeechRecognition extends SpeechRecognition {}

interface SpeechGrammarList {
  length: number;
  addFromURI(src: string, weight?: number): void;
  addFromString(string: string, weight?: number): void;
}

export interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
  interpretation: anyOk;
  emma: Document;
}

interface SpeechRecognitionErrorEvent extends Event {
  error:
    | 'no-speech'
    | 'aborted'
    | 'audio-capture'
    | 'network'
    | 'not-allowed'
    | 'service-not-allowed'
    | 'bad-grammar'
    | 'language-not-supported';
  message: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  isFinal: boolean;
  item(index: number): SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

declare const webkitSpeechRecognition: {
  prototype: webkitSpeechRecognition;
  new (): webkitSpeechRecognition;
};

interface MasteryEnv {
  agentId: string;
  aliases: string[];
  applicationId: string;
  bucket: string;
  corsAllowedOrigins: string;
  hostname: string;
  id: string;
  isProd: boolean;
  origin: string;
  subscription: string;
}

interface IndexedDBColumn {
  name: string;
  keyPath: string;
  options?: IDBIndexParameters;
}

interface IndexedDBStore {
  name: string;
  id: IDBObjectStoreParameters;
  indices: IndexedDBColumn[];
}

export interface IndexedDBConfig {
  databaseName: string;
  version: number;
  stores: IndexedDBStore[];
}

interface MasteryIDB {
  config: {
    databaseName: string;
    version: number;
    stores: IndexedDBStore[];
  };
  init: number;
}

export const IDB_KEY = '__idb';

// ts-unused-exports:disable-next-line
export interface MasteryWindow extends Window {
  Cypress?: unknown;
  masteryWalkmeVariables: Record<string, string | undefined>;
  WalkMePlayerAPI?: {
    toggleMenu(): void;
  };
  _walkmeConfig?: {
    smartLoad: boolean;
    loadPlatform?: string;
    walkme_dont_check_browser?: string;
  };
  React: {
    Fragment: fixMe;
  };
  newrelic: {
    log: (
      s: string,
      kwargs: {
        level: 'debug' | 'info' | 'warn' | 'error' | 'trace';
      },
    ) => void;
    noticeError: (err: string | Error) => void;
    setCustomAttribute?: (
      name: string,
      value: string | number | boolean | null,
      persist?: boolean,
    ) => void;
  };
  electron?: {
    ipc: {
      send: (s: string, d: unknown) => void;
      sendSync: <D extends unknown>(s: string, ...args: unknown[]) => D;
      on: (s: string, cb: (evt: unknown, data: fixMe) => void) => void;
      invoke: (s: string, d: unknown) => void;
    };
    registerWindow: (id: string) => void;
    openWindows: Set<string>;
  };
  FS?: Fullstory;
  masteryFullstoryInit: (orgId: string) => void;
  masteryBoot: {
    BASE_HEADER_HEIGHT: number;
    IS_HEADER_ALLOWED: boolean;
    IS_SIDEBAR_ALLOWED: boolean;
    IS_SIDEBAR_SHOWN_ON_PAGE_LOAD: boolean;
    SIDEBAR_QUERY_PARAM: null | boolean;
  };
  MERCATOR_ENV?: string;
  mastermindFeatureFlags?: Record<string, anyOk>;
  webkitSpeechRecognition: {
    prototype: webkitSpeechRecognition;
    new (): webkitSpeechRecognition;
  };
  SpeechRecognition: {
    prototype: SpeechRecognition;
    new (): SpeechRecognition;
  };
  masteryEnv?: MasteryEnv;
  masteryEnvV2?: {
    tenant: {
      tenantId: string;
      displayName: string;
      subscription: string;
    };
    environment: {
      envKey: string;
      type: 'dev' | 'test' | 'prod';
      hostname: string;
      bucket: string;
      applicationId: string;
      agentId?: string;
      flatFilePublishableKey: string;
      flatFileEnvironmentId: string;
      flatFileLicenseKey: string;
      launchDarklyKey?: string;
    };
  } | null;
  [IDB_KEY]?: MasteryIDB;
  indexedDBCurrentConnection?: IDBDatabase;
  masterfindShouldOpen?: boolean;
}

/** When this variable evaluates to true, it means we have a valid window object. We are probably running inside of a browser (vs Node). */
// eslint-disable-next-line no-restricted-globals
export const HAS_WINDOW = typeof window !== 'undefined';

// eslint-disable-next-line no-restricted-globals
export const win = (HAS_WINDOW ? window : winProxy) as MasteryWindow;

//This code will add a random name to the parent (opener) window and open empty url.
//This is used to focus on the parent tab as win.opener.focus() function does not work.
export const getParentWinObj = (): Window | null => {
  try {
    if (win.opener) {
      let parentWin = win.opener;
      while (parentWin.opener) {
        parentWin = parentWin.opener;
      }
      const randomName = `w-${Date.now().toString()}`;
      parentWin.name = randomName;
      win.open('', randomName);
      return parentWin;
    }
  } catch {
    // Cross-origin openers cannot be used as a navigation target.
    return null;
  }
  return win;
};
